import { Data, List, NeoModel, NotifyUtils } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { reaction } from 'mobx';
import { NotificationDuration } from '../../../App/Models/Enums/NotificationDuration';
import { AppService, Types } from '../../../App/Services/AppService';
import { TextConstants } from '../../../Common/TextConstants';
import FileGroupingFileSpecView from '../../FileGroupingFileSpecs/Views/FileGroupingFileSpecView';
import ManageServiceProviderFileGroupingComponentVM from '../Components/ManageServiceProviderFileGroupingComponentVM';
import SaveServiceProviderFileGroupingCommand from '../Models/Commands/SaveServiceProviderFileGroupingCommand';
import ServiceProviderFileGroupingLookup from '../Models/Lookups/ServiceProviderFileGroupingLookup';
import ServiceProviderSearchCriteria from '../Models/Queries/ServiceProviderSearchCriteria';
import TradeSftpTypeLookup from '../Models/Lookups/TradeSftpTypeLookup';

@NeoModel
export default class ServiceProviderFileGroupingsVM extends Views.ViewModelBase {
    public tradeSftpTypeOptions = new List(TradeSftpTypeLookup);

    constructor(
        public serviceProviderApiClient = AppService.get(Types.ApiClients.ServiceProviderApiClient),
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private navigation = AppService.get(Types.Neo.Routing.NavigationHelper),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService)) {
        super(taskRunner);
    }

    public serviceProviderSearchCriteria = new ServiceProviderSearchCriteria();
    public manageServiceProviderComponentVM = new ManageServiceProviderFileGroupingComponentVM();

    public pageManager = new Data.PageManager(this.serviceProviderSearchCriteria, ServiceProviderFileGroupingLookup,
        this.serviceProviderApiClient.getPagedServiceProviderDetails, {
        pageSize: 10,
        sortBy: "serviceProviderGroupingName",
        sortAscending: true,
        fetchInitial: true,
        initialTaskRunner: this.taskRunner,
    });

    public async initialise() {
        await this.loadTradeSftpTypes();

        reaction(() => this.serviceProviderSearchCriteria.searchCriteria, () => this.pageManager.refreshData(), { fireImmediately: false, delay: 650 });
    }

    private async loadTradeSftpTypes() {
        try {
            const response = await this.serviceProviderApiClient.getTradeSftpTypes();
            this.tradeSftpTypeOptions.set(response.data);
        } catch (error) {
            console.error("Error loading trade SFTP types:", error);
            this.tradeSftpTypeOptions = new List(TradeSftpTypeLookup);
        }
    }

    public viewFileSpecsForBroker(staticServiceProviderFileGroupingID: number, broker: string) {
        this.navigation.navigateToView(FileGroupingFileSpecView, { fileGroupingID: String(staticServiceProviderFileGroupingID), broker: broker, fileSpecID: String(null), backURL: this.navigation.getCurrentViewPath() });
    }

    public async addServiceProviderFileGrouping() {
        await this.manageServiceProviderComponentVM.initialise();
        this.manageServiceProviderComponentVM.addNewServiceProviderInd = true;
    }

    public cancel() {
        this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping = new ServiceProviderFileGroupingLookup();
        this.manageServiceProviderComponentVM.showManageServiceProviderFileGroupingInd = false;
        this.manageServiceProviderComponentVM.addNewServiceProviderInd = false;
        this.manageServiceProviderComponentVM.isReadOnlyInd = false;
    }

    public async clearSearch() {
        this.serviceProviderSearchCriteria.meta.searchCriteria.value = "";
        await this.pageManager.refreshData();
    }

    public async editServiceProviderFileGrouping(fileGroupingLookup: ServiceProviderFileGroupingLookup) {
        this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping.mapFrom(fileGroupingLookup);
        this.manageServiceProviderComponentVM.isReadOnlyInd = true;
        await this.manageServiceProviderComponentVM.initialise();
    }

    private mapFileGroupingToUpdateCommand() {

        this.manageServiceProviderComponentVM.saveDaysOfWeek();

        var command = new SaveServiceProviderFileGroupingCommand().mapFrom(this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping);

        if (!command) {
            return null;
        }

        if (!command.exportsWhenReady && command.exportTime !== undefined && command.exportTime !== null) {
            command.daysOfWeek = this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping.daysOfWeek;
            // Format exportTime as two-digit hour:minute:second (HH:mm:ss) to match backend expectation (8 chars, e.g. "02:00:00")
            try {
                const et: any = command.exportTime;
                const date = et instanceof Date ? et : new Date(et);
                const hh = date.getHours().toString().padStart(2, '0');
                const mm = date.getMinutes().toString().padStart(2, '0');
                const ss = date.getSeconds().toString().padStart(2, '0');
                // Backend requires an 8-character string HH:mm:ss
                command.exportTimeString = `${hh}:${mm}:${ss}`;
            } catch (e) {
                // Fallback to locale string if parsing fails
                command.exportTimeString = command.exportTime.toString();
            }
        }

        command.tradeDistributionTypeId = this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping.tradeDistributionTypeId;

        // Convert to number explicitly
        const samReportingValue = this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping.samReporting;
        command.samReporting = samReportingValue !== null && samReportingValue !== undefined
            ? Number(samReportingValue)
            : null;

        command.sftpBrokerDetailsLookups = this.manageServiceProviderComponentVM.SFTPDetailsList;

        return command;
    }

    public async saveServiceProviderFileGrouping() {
        this.taskRunner.run(async () => {

            if (this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping.staticServiceProviderFileGroupingId !== 0) {
                let groupingId = this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping.staticServiceProviderFileGroupingId;
                this.manageServiceProviderComponentVM.SFTPDetailsList.forEach((detail) => detail.staticServiceProviderFileGroupingId = groupingId);
            }

            // Create the command once
            const command = this.mapFileGroupingToUpdateCommand();

            // Add null check
            if (!command) {
                NotifyUtils.add(TextConstants.Titles.Error, "Failed to create save command", "danger" as any, NotificationDuration.Long);
                return;
            }

             const commandJS = command.toJSObject();

            // Add null check for toJSObject result
            if (!commandJS) {
                NotifyUtils.add(TextConstants.Titles.Error, "Failed to serialize command", "danger" as any, NotificationDuration.Long);
                return;
            }

            // Log the payload for debugging (inspect in browser devtools network/console)
            try {
                console.debug('Saving ServiceProviderFileGrouping payload:', commandJS);
            } catch (e) {
                // no-op
            }

            // Validate exportTimeString: backend requires HH:mm:ss (8 characters)
            if (commandJS.exportTimeString !== undefined && commandJS.exportTimeString !== null) {
                const v = String(commandJS.exportTimeString);
                if (!/^\d{2}:\d{2}:\d{2}$/.test(v) || v.length !== 8) {
                    NotifyUtils.add(TextConstants.Titles.Error, 'Export Time must be in the format "HH:mm:ss" (e.g. "02:00:00").', 'danger' as any, NotificationDuration.Long);
                    return;
                }
            }

            var response = await this.serviceProviderApiClient.saveServiceProviderFileGrouping(commandJS);

            if (response.data.success) {
                await this.pageManager.refreshData();
                NotifyUtils.addSuccess(TextConstants.Titles.Success, "The stockbroker has been successfully saved.", NotificationDuration.Standard);
            }
            else {
                NotifyUtils.add(TextConstants.Titles.Error, response.data.error, "danger" as any, NotificationDuration.Long);
            }

            this.manageServiceProviderComponentVM.showManageServiceProviderFileGroupingInd = false;
            this.manageServiceProviderComponentVM.managedServiceProviderFileGrouping = new ServiceProviderFileGroupingLookup();
            this.manageServiceProviderComponentVM.addNewServiceProviderInd = false;
            this.manageServiceProviderComponentVM.isReadOnlyInd = false;
        })
    }
}